/**
 * The 72h Verified Shortlist offer — ONE definition of the employer-side
 * shortlist price, SLA and candidate count.
 *
 * WHY A DEDICATED MODULE (and not `catalog-onetime-global.ts` / `products.ts`):
 * the existing `employer_*` product family is a JOB-POSTING family — its
 * checkout action pre-creates an `employer_orders` row and its grant publishes
 * a `job_submissions` row (`src/lib/mayar/employer-grant.ts`). A shortlist sale
 * grants no job post, so bolting it onto `EmployerProductKey` would send every
 * purchase down a publish path that has nothing to publish. The shortlist
 * therefore has its OWN key (`ShortlistProductKey`), its own checkout
 * (`src/server/actions/shortlist-checkout.ts`) and its own settlement
 * (`src/lib/mayar/shortlist-grant.ts`), which marks the order paid without
 * publishing anything. The standalone payment link below survives only as a
 * no-JS fallback — see the reconciliation caveat on `shortlistCheckoutUrl()`
 * (`shortlist-checkout-url.ts`, kept out of this module so it stays env-free).
 *
 * WHY THE CONSTANTS LIVE HERE: the price is quoted in two places that must not
 * drift — the outbound email template (`src/lib/outreach/prompt.ts`) and the
 * `/employers/shortlist` landing page (employers zone). Both import from here,
 * so the numeral appears exactly once per currency. This also keeps a raw
 * `USD 149/month`-shaped literal out of `src/lib/outreach/**`, which
 * `pnpm check:outreach-claim-literals` rejects on sight (it scans comments too).
 *
 * Formatting is local rather than imported from `@/lib/ads/pricing` so this
 * module stays dependency-free: it is pulled into the outreach cron worker and
 * the employers zone bundle, neither of which should drag in the ad catalog.
 *
 * PRICE STRUCTURE (2026-09-08) — ONE flat price per shortlist, no success fee.
 *
 * This reverses the deposit + success-fee pair introduced 2026-08-30
 * (docs/plans/revenue-execution-2026-08-30.md work order E6), and it is a
 * correction, not a re-pricing experiment. On 2026-09-07 the ICP offer went to
 * 16 named humans quoting a flat price for the next shortlist; the landing page
 * and this module were simultaneously quoting a deposit at 2.7x that headline
 * plus a success fee the email never mentioned. Same product, same week, same
 * buyers. The retired figures are recorded in
 * `docs/audit/archive/freemium-production-audit-2026-09-08.md` §9 and deliberately not
 * repeated here — the live values are the two constants below, and a superseded
 * number surviving in a comment is its own well-documented incident class
 * (`scripts/check-outreach-claim-literals.ts`). Kelvin's call: the price the
 * buyers were actually told is the real one.
 *
 *   - `SHORTLIST_PRICE_*` — the whole price, charged at checkout (the amount
 *     `SHORTLIST_PRODUCTS.shortlist_pilot` bills through Mayar QRIS, see
 *     `src/lib/mayar/products.ts`). Creditable on a missed SLA — see the
 *     DELIVERY GUARANTEE block below `SHORTLIST_REPLACEMENT_DAYS`.
 *   - There is deliberately NO success-fee constant. A figure nothing charges
 *     and nothing invoices is a claim, and this one had already drifted from
 *     the only channel a buyer had actually read. If a success fee comes back
 *     it comes back with an invoicing path and one number in one place.
 *
 * WHY THE OUTBOUND EMAIL NO LONGER CARRIES A LITERAL. The ICP offer copy
 * (`src/lib/outreach/icp-shortlist-email.ts`) used to hardcode its own price
 * string, which is how the two channels drifted at all. It now interpolates
 * `shortlistOfferPriceLine()` like every other outbound surface, so this module
 * is the single writer of the number and divergence is unrepresentable rather
 * than merely discouraged.
 *
 * DELIVERY GUARANTEE — trigger, remedy, enforcement, owner. Tightened
 * 2026-09-02 from the original deposit-REFUND promise (Kelvin's own call —
 * this is a solo, cash-constrained operator, and a cash refund on a
 * subjective "none were worth interviewing" trigger with no claim deadline
 * was an open-ended cash-flow risk this business cannot absorb):
 *   - TRIGGER: `SHORTLIST_SLA_HOURS` (72h) elapses from role-brief intake
 *     without `SHORTLIST_CANDIDATE_COUNT` candidates delivered. OBJECTIVE and
 *     entirely inside the operator's own control — never the employer's
 *     subjective opinion of who was delivered. If the SLA is met, the
 *     price is NOT creditable — the sourcing/screening work is already
 *     done regardless of whether the employer chooses to interview anyone.
 *   - CLAIM WINDOW: `SHORTLIST_REFUND_CLAIM_WINDOW_DAYS` (7) days from the
 *     missed deadline, not 30 — a missed SLA is knowable immediately, so
 *     there is no reason for the claim window to be as long as a
 *     satisfaction-based dispute would need.
 *   - REMEDY: CREDIT toward the employer's next shortlist purchase, never a
 *     cash refund. No money moves backward, which is also why fulfilling
 *     this needs no Mayar refund call — see ENFORCEMENT.
 *   - ENFORCEMENT TODAY: manual, not automated, and no code in this repo
 *     tracks a credit balance yet (`employer_orders.status` has no
 *     credit-related value — do not repurpose `'refunded'` for this, it
 *     would misstate that money moved when it didn't). Honouring a claim
 *     today means noting it by hand and applying it as a manual discount at
 *     the employer's NEXT checkout. A real credits ledger is future work,
 *     not part of this change.
 *   - OWNER: Kelvin (founder/operator) — unchanged from the prior version of
 *     this comment; still the only person who can process a claim today.
 */

/** Verified, interested candidates delivered per shortlist. */
export const SHORTLIST_CANDIDATE_COUNT = 5;

/** Delivery SLA, in hours, from role brief to shortlist. Supporting detail —
 * the offer is sold on verification, not on speed (see `prompt.ts`). */
export const SHORTLIST_SLA_HOURS = 72;

/**
 * The whole shortlist price collected at checkout, USD minor units (cents).
 * Credited toward the next shortlist when the trigger in the DELIVERY
 * GUARANTEE block above is met. Never refunded in cash — see that block.
 *
 * This is the figure quoted to the 16 ICP recipients on 2026-09-07, which is
 * why it is this one and not a round number — see PRICE STRUCTURE above.
 */
export const SHORTLIST_PRICE_USD_MINOR = 14_900;

/**
 * The whole shortlist price, IDR major units (rupiah).
 *
 * The exact rupiah figure the ICP offer email quoted beside the USD one — a
 * stated pair, not a spot conversion of it, so the two currencies cannot drift
 * against each other the way a computed rate would.
 *
 * No live Mayar product exists for it yet (docs/plans/revenue-execution-2026-08-30.md
 * E6, step 5 — still deliberately NOT created here); whoever creates it must
 * set the product's price to this number, exactly as the subscription price
 * note below describes.
 */
export const SHORTLIST_PRICE_IDR = 2_250_000;

/** Free-replacement window, in days, after a hire made through the shortlist.
 * Unrelated to the delivery guarantee below — this is about a HIRE not
 * working out, not about the shortlist itself missing its SLA. */
export const SHORTLIST_REPLACEMENT_DAYS = 60;

/**
 * Days from a missed `SHORTLIST_SLA_HOURS` deadline within which the
 * employer must claim the credit — see the DELIVERY GUARANTEE block in this
 * file's module comment for the full trigger/remedy.
 */
export const SHORTLIST_REFUND_CLAIM_WINDOW_DAYS = 7;

/** Subscription, USD minor units (cents) per month. */
export const SHORTLIST_SUBSCRIPTION_USD_MINOR = 49_900;

/**
 * Subscription, IDR major units (rupiah) per month.
 *
 * NOT a spot conversion of the USD price — it is the Mayar product's own
 * rupiah price (docs/plans/money-path-execution-2026-08-29.md, ClickOps 1).
 * Without this an Indonesian reader was quoted `Rp 2.500.000` for the pilot
 * and `$499/bulan` for the subscription side by side: two currencies in one
 * price ladder, which a buyer cannot compare.
 */
export const SHORTLIST_SUBSCRIPTION_IDR = 8_000_000;

/** Roles covered by one subscription seat. */
export const SHORTLIST_SUBSCRIPTION_ACTIVE_ROLES = 2;

export type OfferLocale = "en" | "id";

/** Only ever fed the whole-dollar constants above (pinned by tests). */
function formatUsdMinor(minor: number): string {
	return `$${(minor / 100).toLocaleString("en-US")}`;
}

function formatIdrMajor(amount: number): string {
	return `Rp ${amount.toLocaleString("id-ID")}`;
}

/**
 * The shortlist price in the currency the recipient actually pays in.
 * Indonesian leads are quoted IDR (the Mayar rail's home currency); everyone
 * else USD.
 */
export function shortlistPriceLabel(locale: OfferLocale): string {
	return locale === "id"
		? formatIdrMajor(SHORTLIST_PRICE_IDR)
		: formatUsdMinor(SHORTLIST_PRICE_USD_MINOR);
}

/**
 * The whole pricing quote as ONE sentence fragment: the flat price, what it
 * covers, and the replacement window. This is the single thing outbound copy
 * and the landing page should ever interpolate for "the price".
 *
 * ONE numeral, deliberately. The previous version put two prices side by side
 * and had to warn callers never to join them with a hyphen, because
 * `$400-$2,000` reads as a salary range and `findForbiddenClaims()` tears up
 * the draft over it (`src/lib/outreach/forbidden-claims.ts`). A single price
 * removes that footgun rather than documenting it.
 */
export function shortlistOfferPriceLine(locale: OfferLocale): string {
	const price = shortlistPriceLabel(locale);
	return locale === "id"
		? `${price} per shortlist, tanpa fee tambahan kalau Anda merekrut (jadi kredit shortlist berikutnya kalau kami lewat dari ${SHORTLIST_SLA_HOURS} jam pengiriman, garansi ganti ${SHORTLIST_REPLACEMENT_DAYS} hari)`
		: `${price} per shortlist, with no fee if you hire (credited toward your next shortlist if we miss the ${SHORTLIST_SLA_HOURS}h delivery, ${SHORTLIST_REPLACEMENT_DAYS}-day replacement)`;
}

/**
 * The subscription price line. NOTE: never render this as a range next to the
 * deposit or fee price — `findForbiddenClaims()` matches any `$N-N` shape
 * as a `foreign_salary` claim and tears up the whole draft
 * (`src/lib/outreach/forbidden-claims.ts`).
 */
export function shortlistSubscriptionPriceLabel(locale: OfferLocale): string {
	return locale === "id"
		? `${formatIdrMajor(SHORTLIST_SUBSCRIPTION_IDR)}/bulan`
		: `${formatUsdMinor(SHORTLIST_SUBSCRIPTION_USD_MINOR)}/month`;
}
